import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Boxes, 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  ChevronRight, 
  ChevronDown,
  Home,
  Layers,
  Archive,
  Box,
  Package,
  MapPin
} from 'lucide-react';
import { Button } from '@/components/common/Button';
import { useToast } from '@/hooks/useToast';
import { storageLocationsApi } from '@/api/storageLocationsApi';
import type { StorageLocation } from '@/types/api';

// Location type icons
const locationTypeIcons: Record<string, React.ElementType> = {
  ROOM: Home,
  SHELF: Layers,
  BIN: Archive,
  CONTAINER: Box,
  DRAWER: Package,
  CABINET: Boxes,
};

// Recursive tree item component
interface TreeItemProps {
  location: StorageLocation;
  level: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onEdit: (location: StorageLocation) => void;
  onDelete: (id: string) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
}

function TreeItem({ 
  location, 
  level, 
  selectedId, 
  onSelect, 
  onEdit, 
  onDelete,
  expandedIds,
  onToggleExpand 
}: TreeItemProps) {
  const Icon = locationTypeIcons[location.locationType] || Box;
  const isExpanded = expandedIds.has(location.id);
  const hasChildren = location.children && location.children.length > 0;
  const isSelected = selectedId === location.id;

  return (
    <div>
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
          isSelected 
            ? 'bg-amber-100 dark:bg-amber-450/10 border border-amber-200 dark:border-amber-450/20' 
            : 'hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700'
        }`}
        style={{ paddingLeft: `${12 + level * 20}px` }}
        onClick={() => onSelect(location.id)}
      >
        {/* Expand/collapse button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(location.id);
          }}
          className={`p-0.5 rounded hover:bg-gray-200 dark:hover:bg-charcoal-600 transition-colors ${
            hasChildren ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500 dark:text-warm-sand" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500 dark:text-warm-sand" />
          )}
        </button>

        {/* Icon */}
        <Icon className={`w-4 h-4 ${
          isSelected ? 'text-amber-600 dark:text-amber-450' : 'text-gray-500 dark:text-warm-sand'
        }`} />

        {/* Name */}
        <span className={`flex-1 text-sm truncate ${
          isSelected ? 'text-amber-700 dark:text-amber-450 font-medium' : 'text-gray-700 dark:text-warm-cream'
        }`}>
          {location.name}
        </span>

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(location);
            }}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-charcoal-600 text-gray-500 dark:text-warm-sand"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(location.id);
            }}
            className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-500/10 text-gray-500 dark:text-warm-sand hover:text-red-600 dark:hover:text-red-600 dark:text-red-400"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {location.children.map((child) => (
            <TreeItem
              key={child.id}
              location={child}
              level={level + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              onEdit={onEdit}
              onDelete={onDelete}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Add/Edit Modal
interface LocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingLocation: StorageLocation | null;
  parentId: string | null;
  locations: StorageLocation[];
}

function LocationModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  editingLocation, 
  parentId,
  locations 
}: LocationModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [locationType, setLocationType] = useState('ROOM');
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState(0);
  const [loading, setLoading] = useState(false);
  const { error: showError, success: showSuccess } = useToast();

  useEffect(() => {
    if (editingLocation) {
      setName(editingLocation.name);
      setDescription(editingLocation.description || '');
      setLocationType(editingLocation.locationType);
      setSelectedParentId(editingLocation.parentId);
      setSortOrder(editingLocation.sortOrder);
    } else {
      setName('');
      setDescription('');
      setLocationType('ROOM');
      setSelectedParentId(parentId);
      setSortOrder(0);
    }
  }, [editingLocation, parentId, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      showError('Name is required');
      return;
    }

    try {
      setLoading(true);
      const data = {
        name: name.trim(),
        description: description.trim() || undefined,
        locationType,
        parentId: selectedParentId || undefined,
        sortOrder,
      };

      if (editingLocation) {
        await storageLocationsApi.update(editingLocation.id, data);
        showSuccess('Location updated successfully');
      } else {
        await storageLocationsApi.create(data);
        showSuccess('Location created successfully');
      }
      onSuccess();
      onClose();
    } catch (error) {
      showError(editingLocation ? 'Failed to update location' : 'Failed to create location');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Flatten locations for parent dropdown
  const flattenLocations = (locs: StorageLocation[], level = 0): { id: string; name: string; level: number }[] => {
    const result: { id: string; name: string; level: number }[] = [];
    for (const loc of locs) {
      // Don't include the current editing location or its descendants as parent options
      if (editingLocation && (loc.id === editingLocation.id || isDescendant(loc, editingLocation.id))) {
        continue;
      }
      result.push({ id: loc.id, name: loc.name, level });
      if (loc.children) {
        result.push(...flattenLocations(loc.children, level + 1));
      }
    }
    return result;
  };

  const isDescendant = (location: StorageLocation, targetId: string): boolean => {
    if (!location.children) return false;
    for (const child of location.children) {
      if (child.id === targetId || isDescendant(child, targetId)) return true;
    }
    return false;
  };

  const flatLocations = flattenLocations(locations);

  return (
    <div className="modal-backdrop flex items-center justify-center p-4">
      <div className="bg-white dark:bg-charcoal-800 rounded-2xl border border-gray-200 dark:border-charcoal-700 shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200 dark:border-charcoal-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-warm-cream">
            {editingLocation ? 'Edit Location' : 'Add Location'}
          </h2>
          <p className="text-sm text-gray-500 dark:text-warm-sand mt-1">
            {editingLocation ? 'Update storage location details' : 'Create a new storage location'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-warm-sand mb-2">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Studio Room, Shelf A, Bin 1"
              className="input"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-warm-sand mb-2">
              Type *
            </label>
            <select
              value={locationType}
              onChange={(e) => setLocationType(e.target.value)}
              className="input"
            >
              <option value="ROOM">Room</option>
              <option value="SHELF">Shelf</option>
              <option value="BIN">Bin</option>
              <option value="CONTAINER">Container</option>
              <option value="DRAWER">Drawer</option>
              <option value="CABINET">Cabinet</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-warm-sand mb-2">
              Parent Location
            </label>
            <select
              value={selectedParentId || ''}
              onChange={(e) => setSelectedParentId(e.target.value || null)}
              className="input"
            >
              <option value="">None (Top Level)</option>
              {flatLocations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {'  '.repeat(loc.level)}└─ {loc.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-warm-sand mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
              className="input resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-warm-sand mb-2">
              Sort Order
            </label>
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              className="input"
              min={0}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={loading}
            >
              {loading ? 'Saving...' : editingLocation ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Main page component
export function StorageLocations() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState<StorageLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<StorageLocation | null>(null);
  const [modalParentId, setModalParentId] = useState<string | null>(null);
  const { error: showError, success: showSuccess } = useToast();

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const data = await storageLocationsApi.getTree();
      setLocations(data);
    } catch (error) {
      showError('Failed to load storage locations');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this location?')) return;
    try {
      await storageLocationsApi.delete(id);
      showSuccess('Location deleted successfully');
      if (selectedId === id) {
        setSelectedId(null);
      }
      fetchLocations();
    } catch (error) {
      showError('Failed to delete location');
    }
  };

  const handleAdd = (parentId: string | null = null) => {
    setEditingLocation(null);
    setModalParentId(parentId);
    setIsModalOpen(true);
  };

  const handleEdit = (location: StorageLocation) => {
    setEditingLocation(location);
    setModalParentId(null);
    setIsModalOpen(true);
  };

  // Find location by ID recursively
  const findLocation = (locs: StorageLocation[], id: string): StorageLocation | null => {
    for (const loc of locs) {
      if (loc.id === id) return loc;
      if (loc.children) {
        const found = findLocation(loc.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const selectedLocation = selectedId ? findLocation(locations, selectedId) : null;

  const handleSelectLocation = (id: string) => {
    setSelectedId(id);
    navigate(`/storage-locations/${id}`);
  };

  // Filter locations based on search
  const filterLocations = (locs: StorageLocation[]): StorageLocation[] => {
    if (!searchQuery.trim()) return locs;
    
    return locs.map(loc => {
      const matches = loc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                     loc.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      let filteredChildren: StorageLocation[] = [];
      if (loc.children) {
        filteredChildren = filterLocations(loc.children);
      }
      
      if (matches || filteredChildren.length > 0) {
        return { ...loc, children: filteredChildren };
      }
      return null;
    }).filter(Boolean) as StorageLocation[];
  };

  const filteredLocations = filterLocations(locations);

  return (
    <div className="h-[calc(100vh-8rem)] flex gap-6">
      {/* Left Panel - Location Tree */}
      <div className="w-80 flex flex-col bg-white dark:bg-charcoal-800 rounded-2xl border border-gray-200 dark:border-charcoal-700 overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-charcoal-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Boxes className="w-5 h-5 text-amber-600 dark:text-amber-450" />
              <h2 className="font-semibold text-gray-900 dark:text-warm-cream">Locations</h2>
            </div>
            <Button 
              variant="primary" 
              size="sm"
              onClick={() => handleAdd()}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-warm-sand" />
            <input
              type="text"
              placeholder="Search locations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-charcoal-900 border border-gray-200 dark:border-charcoal-700 rounded-lg text-gray-900 dark:text-warm-cream placeholder-gray-400 dark:placeholder-warm-sand focus:ring-2 focus:ring-amber-450/50 focus:border-amber-450"
            />
          </div>
        </div>

        {/* Tree */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {loading ? (
            <div className="p-4 text-center text-gray-500 dark:text-warm-sand">
              Loading...
            </div>
          ) : filteredLocations.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-warm-sand">
              {searchQuery ? 'No locations found' : 'No locations yet'}
            </div>
          ) : (
            filteredLocations.map((location) => (
              <TreeItem
                key={location.id}
                location={location}
                level={0}
                selectedId={selectedId}
                onSelect={handleSelectLocation}
                onEdit={handleEdit}
                onDelete={handleDelete}
                expandedIds={expandedIds}
                onToggleExpand={handleToggleExpand}
              />
            ))
          )}
        </div>

        {/* Add Room Button */}
        <div className="p-4 border-t border-gray-200 dark:border-charcoal-700">
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => handleAdd()}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Room
          </Button>
        </div>
      </div>

      {/* Right Panel - Details */}
      <div className="flex-1 bg-white dark:bg-charcoal-800 rounded-2xl border border-gray-200 dark:border-charcoal-700 overflow-hidden">
        {selectedLocation ? (
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-charcoal-700">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-amber-100 dark:bg-amber-450/10 border border-amber-200 dark:border-amber-450/20">
                    {(() => {
                      const Icon = locationTypeIcons[selectedLocation.locationType] || Box;
                      return <Icon className="w-8 h-8 text-amber-600 dark:text-amber-450" />;
                    })()}
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-warm-cream">
                      {selectedLocation.name}
                    </h1>
                    <p className="text-gray-500 dark:text-warm-sand">
                      {selectedLocation.locationTypeDisplay}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleEdit(selectedLocation)}
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleAdd(selectedLocation.id)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Child
                  </Button>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="flex-1 p-6 space-y-6 overflow-y-auto">
              {/* Info Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-charcoal-900 rounded-xl border border-gray-200 dark:border-charcoal-700">
                  <p className="text-sm text-gray-500 dark:text-warm-sand">Type</p>
                  <p className="text-lg font-medium text-gray-900 dark:text-warm-cream">
                    {selectedLocation.locationTypeDisplay}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-charcoal-900 rounded-xl border border-gray-200 dark:border-charcoal-700">
                  <p className="text-sm text-gray-500 dark:text-warm-sand">Status</p>
                  <p className="text-lg font-medium text-gray-900 dark:text-warm-cream">
                    {selectedLocation.isActive ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-600 dark:text-green-400 border border-green-200 dark:border-green-500/20">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-500/10 text-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-500/20">
                        Inactive
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {selectedLocation.description && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-warm-sand mb-2">
                    Description
                  </h3>
                  <p className="text-gray-900 dark:text-warm-cream">
                    {selectedLocation.description}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-charcoal-900 rounded-xl border border-gray-200 dark:border-charcoal-700">
                  <p className="text-sm text-gray-500 dark:text-warm-sand">Sort Order</p>
                  <p className="text-lg font-medium text-gray-900 dark:text-warm-cream">
                    {selectedLocation.sortOrder}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-charcoal-900 rounded-xl border border-gray-200 dark:border-charcoal-700">
                  <p className="text-sm text-gray-500 dark:text-warm-sand">Children</p>
                  <p className="text-lg font-medium text-gray-900 dark:text-warm-cream">
                    {selectedLocation.children?.length || 0}
                  </p>
                </div>
              </div>

              {/* Created At */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-warm-sand mb-2">
                  Created
                </h3>
                <p className="text-gray-900 dark:text-warm-cream">
                  {new Date(selectedLocation.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>

              {/* Placeholder for Supplies in this location */}
              <div className="p-6 bg-gray-50 dark:bg-charcoal-900 rounded-xl border border-gray-200 dark:border-charcoal-700 border-dashed">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="w-5 h-5 text-gray-400 dark:text-warm-sand" />
                  <h3 className="font-medium text-gray-900 dark:text-warm-cream">
                    Supplies in this Location
                  </h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-warm-sand">
                  Supplies stored in this location will be shown here.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center p-8">
              <div className="p-4 rounded-full bg-gray-100 dark:bg-charcoal-700 inline-flex mb-4">
                <Boxes className="w-12 h-12 text-gray-400 dark:text-warm-sand" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-warm-cream mb-2">
                Select a Location
              </h3>
              <p className="text-gray-500 dark:text-warm-sand max-w-sm">
                Choose a location from the tree to view details, or create a new one to get started.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      <LocationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchLocations}
        editingLocation={editingLocation}
        parentId={modalParentId}
        locations={locations}
      />
    </div>
  );
}
